const modal = document.querySelector('#ravessa-search');
const openers = [...document.querySelectorAll('[data-search-open]')];
const HISTORY_KEY = 'ravessa.search.history.v1';
const clockIcon =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="8"/><path d="M12 7v5l3 2"/></svg>';
let returnFocus = null;
let savedStyle = null;
let scrollTop = 0;
let background = [];
let modalController = null;
let rateLimitUntil = 0;
const modalOpen = () => modal && !modal.hidden;
const fitViewport = () => {
    if (!modalOpen()) return;
    const viewport = window.visualViewport;
    const height = viewport?.height || window.innerHeight;
    modal.style.setProperty('--search-vh', `${height}px`);
    modal.style.top = `${viewport?.offsetTop || 0}px`;
    modal.classList.toggle('is-compact', height < 600);
};
window.addEventListener('resize', fitViewport);
window.visualViewport?.addEventListener('resize', fitViewport);
window.visualViewport?.addEventListener('scroll', fitViewport);
const historyTerms = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
        return Array.isArray(saved)
            ? saved
                  .filter(
                      (term) => typeof term === 'string' && term.length >= 2 && term.length <= 80,
                  )
                  .slice(0, 6)
            : [];
    } catch {
        return [];
    }
};
const remember = (value) => {
    const term = value.trim().slice(0, 80);
    if (term.length < 2) return;
    try {
        const terms = historyTerms().filter(
            (item) => item.toLocaleLowerCase('pl') !== term.toLocaleLowerCase('pl'),
        );
        localStorage.setItem(HISTORY_KEY, JSON.stringify([term, ...terms].slice(0, 6)));
        window.dispatchEvent(new Event('ravessa:search-history'));
    } catch {
        /* Search remains usable when storage is unavailable. */
    }
};
const emit = (type, detail) =>
    document.dispatchEvent(new CustomEvent('ravessa:search', { detail: { type, ...detail } }));

const close = (restoreFocus = true) => {
    if (!modalOpen()) return;
    modalController.cancel();
    modal.classList.remove('is-open');
    modal.hidden = true;
    modal.inert = true;
    modal.setAttribute('aria-hidden', 'true');
    background.forEach(({ element, inert }) => {
        element.inert = inert;
    });
    background = [];
    document.documentElement.classList.remove('ravessa-search-open');
    if (savedStyle === null) document.body.removeAttribute('style');
    else document.body.setAttribute('style', savedStyle);
    window.scrollTo({ top: scrollTop, behavior: 'instant' });
    openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (restoreFocus && returnFocus && returnFocus.isConnected)
        returnFocus.focus({ preventScroll: true });
};

const createSearch = (instance) => {
    const input = instance.querySelector('[data-search-input]');
    const clear = instance.querySelector('[data-search-clear]');
    const results = instance.querySelector('[data-search-results]');
    const status = instance.querySelector('[data-search-status]');
    const fullPage = instance.dataset.searchInstance === 'page';
    const cache = new Map();
    let timer = null;
    let retryTimer = null;
    let request = null;
    let sequence = 0;
    let renderedQuery = input.value;
    let state = {
        q: input.value,
        category: instance.dataset.initialCategory || '0',
        sale: instance.dataset.initialSale === '1',
        sort: instance.dataset.initialSort || 'bestsellers',
        page: Number(instance.dataset.initialPage) || 1,
    };
    const active = () => fullPage || modalOpen();
    const cancel = () => {
        sequence += 1;
        clearTimeout(timer);
        clearTimeout(retryTimer);
        if (request) request.abort();
        request = null;
        results.setAttribute('aria-busy', 'false');
    };
    const url = () => {
        const target = new URL(instance.dataset.searchHome, location.href);
        target.searchParams.set('s', input.value.trim());
        target.searchParams.set('post_type', 'product');
        if (Number(state.category)) target.searchParams.set('category', state.category);
        if (state.sale) target.searchParams.set('sale', '1');
        if (state.sort !== 'bestsellers') target.searchParams.set('sort', state.sort);
        if (state.page > 1) target.searchParams.set('paged', state.page);
        return target;
    };
    const renderHistory = () => {
        const host = results.querySelector('[data-search-history]');
        if (!host) return;
        host.replaceChildren();
        const terms = historyTerms();
        if (!terms.length) return;
        const section = document.createElement('div');
        section.className = 'SearchStudio__history';
        const heading = document.createElement('div');
        heading.className = 'SearchStudio__historyHeading';
        const label = document.createElement('span');
        label.className = 'SearchStudio__eyebrow';
        label.textContent = 'OSTATNIO SZUKANE';
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'Button--no-style';
        remove.dataset.searchHistoryClear = '';
        remove.textContent = 'Wyczyść';
        remove.setAttribute('aria-label', 'Wyczyść historię wyszukiwania na tym urządzeniu');
        heading.append(label, remove);
        const list = document.createElement('div');
        list.className = 'SearchStudio__historyList';
        terms.forEach((term) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'Button--no-style';
            button.dataset.searchQuery = term;
            button.innerHTML = clockIcon;
            const text = document.createElement('span');
            text.textContent = term;
            button.append(text);
            list.append(button);
        });
        section.append(heading, list);
        host.append(section);
    };
    const render = (data, focusKey) => {
        results.innerHTML = data.html;
        renderedQuery = data.query;
        results.setAttribute('aria-busy', 'false');
        status.textContent = data.idle
            ? 'Odkryj produkty i kategorie Ravessy.'
            : `Znaleziono: ${data.count_label}.`;
        renderHistory();
        if (focusKey) {
            const target = [...results.querySelectorAll('[data-search-focus]')].find(
                (element) => element.dataset.searchFocus === focusKey,
            );
            if (target) target.focus({ preventScroll: true });
        }
        if (fullPage) {
            const heading = instance.querySelector('h1');
            heading.textContent = data.query ? `Twój wybór: ${data.query}` : 'Odkrywaj Ravessę';
            state.page = data.page;
            history.replaceState(null, '', url());
        }
        emit('results', { query: data.query, total: data.total });
    };
    const showError = (message, retryAfter = 0) => {
        clearTimeout(retryTimer);
        results.replaceChildren();
        results.setAttribute('aria-busy', 'false');
        const box = document.createElement('div');
        box.className = 'SearchStudio__empty';
        const heading = document.createElement('h4');
        heading.textContent = retryAfter ? 'Zróbmy krótką przerwę' : 'Spróbujmy jeszcze raz';
        const text = document.createElement('p');
        text.textContent =
            message || 'Nie udało się wczytać wyników. Sprawdź połączenie i spróbuj ponownie.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'Button--no-style SearchStudio__suggestion';
        retry.dataset.searchRetry = '';
        retry.textContent = 'Ponów wyszukiwanie';
        const fallback = document.createElement('a');
        fallback.className = 'SearchStudio__shopLink';
        fallback.href = url();
        fallback.textContent = 'Otwórz stronę wyników';
        box.append(heading, text, retry);
        if (!retryAfter) box.append(fallback);
        results.append(box);
        status.textContent = text.textContent;
        if (retryAfter) {
            const tick = () => {
                const remaining = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000));
                retry.disabled = remaining > 0;
                retry.textContent = remaining ? `Spróbuj ponownie za ${remaining} s` : 'Ponów wyszukiwanie';
                if (remaining) retryTimer = setTimeout(tick, 250);
                else {
                    text.textContent = 'Możesz już ponowić wyszukiwanie.';
                    status.textContent = text.textContent;
                }
            };
            tick();
        }
    };
    const search = (delay = 0, page = 1) => {
        cancel();
        state.q = input.value.trim().slice(0, 80);
        state.page = page;
        clear.hidden = !input.value;
        const current = sequence;
        const key = JSON.stringify(state);
        const focused = results.contains(document.activeElement)
            ? document.activeElement.dataset.searchFocus
            : null;
        // Replace old results immediately so a slower response can never be selected for a new query.
        results.innerHTML =
            '<div class="SearchSkeleton" aria-hidden="true"><span></span><span></span><span></span><span></span></div>';
        results.setAttribute('aria-busy', 'true');
        status.textContent = 'Szukamy dla Ciebie…';
        timer = setTimeout(async () => {
            if (!active() || current !== sequence) return;
            const cached = cache.get(key);
            if (cached && Date.now() - cached.time < 30000) {
                render(cached.data, focused);
                return;
            }
            const remaining = Math.ceil((rateLimitUntil - Date.now()) / 1000);
            if (remaining > 0) {
                showError(`Wykonano wiele wyszukiwań w krótkim czasie. Spróbuj ponownie za ${remaining} s.`, remaining);
                return;
            }
            const controller = new AbortController();
            request = controller;
            const timeout = setTimeout(() => controller.abort(), 15000);
            const endpoint = new URL(instance.dataset.searchEndpoint, location.href);
            endpoint.search = new URLSearchParams({
                action: 'ravessa_search',
                q: state.q,
                category: state.category,
                sale: state.sale ? '1' : '0',
                sort: state.sort,
                page: String(page),
                view: fullPage ? 'page' : 'overlay',
            }).toString();
            try {
                const response = await fetch(endpoint, {
                    credentials: 'same-origin',
                    signal: controller.signal,
                    cache: 'no-store',
                });
                const json = await response.json().catch(() => null);
                if (current !== sequence || !active()) return;
                if (!response.ok || !json?.success || typeof json.data?.html !== 'string') {
                    const error = new Error('Search unavailable');
                    error.searchMessage = json?.data?.message;
                    if (response.status === 429) {
                        const wait = Number(json?.data?.retry_after || response.headers.get('Retry-After'));
                        error.searchRetryAfter = Number.isFinite(wait) && wait > 0 ? Math.min(3600, Math.ceil(wait)) : 10;
                        rateLimitUntil = Math.max(rateLimitUntil, Date.now() + error.searchRetryAfter * 1000);
                        error.searchMessage ||= `Wykonano wiele wyszukiwań w krótkim czasie. Spróbuj ponownie za ${error.searchRetryAfter} s.`;
                    }
                    throw error;
                }
                if (cache.size >= 24) cache.delete(cache.keys().next().value);
                cache.set(key, { data: json.data, time: Date.now() });
                render(json.data, focused);
            } catch (error) {
                if (current === sequence && active())
                    showError(
                        error.name === 'AbortError'
                            ? 'Odpowiedź trwa zbyt długo. Spróbuj ponownie.'
                            : error.searchMessage,
                        error.searchRetryAfter,
                    );
            } finally {
                clearTimeout(timeout);
                if (request === controller) request = null;
            }
        }, delay);
    };
    const resetFilters = () => {
        state = { ...state, category: '0', sale: false, sort: 'bestsellers', page: 1 };
    };
    input.addEventListener('input', (event) => {
        if (event.isComposing) return;
        if (!input.value.trim()) resetFilters();
        search(220);
    });
    input.addEventListener('compositionend', () => search(220));
    instance.addEventListener('submit', (event) => {
        if (event.target.matches('[data-search-form]')) {
            event.preventDefault();
            remember(input.value);
            state.page = 1;
            if (fullPage || Date.now() < rateLimitUntil) search();
            else location.assign(url());
        } else if (event.target.matches('[data-search-filters]')) {
            event.preventDefault();
            search();
        }
    });
    instance.addEventListener('change', (event) => {
        if (!event.target.closest('[data-search-filters]')) return;
        const { name, value, checked } = event.target;
        if (name === 'sort') state.sort = value;
        else if (name === 'sale') state.sale = checked;
        search();
    });
    instance.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = event.target.closest('button,a');
        if (!target) return;
        if (target.matches('[data-search-clear]')) {
            input.value = '';
            resetFilters();
            search();
            input.focus();
        } else if (target.matches('[data-search-query]')) {
            event.preventDefault();
            input.value = target.dataset.searchQuery;
            resetFilters();
            remember(input.value);
            search();
            input.focus({ preventScroll: true });
        } else if (target.matches('[data-search-category]')) {
            event.preventDefault();
            state.category =
                state.category === target.dataset.searchCategory
                    ? '0'
                    : target.dataset.searchCategory;
            search();
        } else if (target.matches('[data-search-reset]')) {
            event.preventDefault();
            resetFilters();
            search();
            input.focus({ preventScroll: true });
        } else if (target.matches('[data-search-page]')) {
            event.preventDefault();
            search(0, Number(target.dataset.searchPage));
            input.scrollIntoView({ block: 'start', behavior: 'auto' });
        } else if (target.matches('[data-search-history-clear]')) {
            try {
                localStorage.removeItem(HISTORY_KEY);
            } catch {
                /* Storage may be blocked. */
            }
            window.dispatchEvent(new Event('ravessa:search-history'));
            input.focus({ preventScroll: true });
        } else if (target.matches('[data-search-retry]')) {
            search();
        } else if (target.matches('[data-search-result],[data-search-all]')) {
            remember(renderedQuery);
            emit('select', { query: renderedQuery, url: target.href });
        }
    });
    instance.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const links = [...results.querySelectorAll('[data-search-result]')];
        if (!links.length) return;
        const index = links.indexOf(document.activeElement);
        if (event.target !== input && index === -1) return;
        event.preventDefault();
        if (event.key === 'ArrowUp') (index <= 0 ? input : links[index - 1]).focus();
        else links[Math.min(index + 1, links.length - 1)].focus();
    });
    window.addEventListener('ravessa:search-history', renderHistory);
    window.addEventListener('pageshow', (event) => {
        if (event.persisted && active()) {
            cache.clear();
            search(0, state.page);
        }
    });
    instance.classList.add('is-enhanced');
    renderHistory();
    return { search, cancel, input };
};

document.querySelectorAll('[data-search-instance]').forEach((instance) => {
    const controller = createSearch(instance);
    if (instance.dataset.searchInstance === 'overlay') modalController = controller;
});

const open = (event) => {
    if (!modal || !modalController || document.querySelector('#sidebar-cart.active')) return;
    if (event) event.preventDefault();
    if (!modalOpen()) {
        returnFocus = document.activeElement;
        savedStyle = document.body.getAttribute('style');
        scrollTop = window.scrollY;
        Object.assign(document.body.style, {
            position: 'fixed',
            top: `-${scrollTop}px`,
            left: '0',
            right: '0',
            width: '100%',
        });
        document.documentElement.classList.add('ravessa-search-open');
        background = [...document.body.children]
            .filter(
                (element) =>
                    element !== modal && !['SCRIPT', 'LINK', 'STYLE'].includes(element.tagName),
            )
            .map((element) => ({ element, inert: element.inert }));
        background.forEach(({ element }) => {
            element.inert = true;
        });
        modal.hidden = false;
        modal.inert = false;
        modal.setAttribute('aria-hidden', 'false');
        fitViewport();
        requestAnimationFrame(() => {
            if (modalOpen()) modal.classList.add('is-open');
        });
        openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        modalController.search();
    }
    modalController.input.focus({ preventScroll: true });
};
openers.forEach((button) => button.addEventListener('click', open));
modal?.addEventListener('click', (event) => {
    if (event.target.closest('[data-search-close]')) close();
});
document.addEventListener('keydown', (event) => {
    const typing = event.target.closest('input,textarea,select,[contenteditable=true]');
    if (
        ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') ||
        (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey)
    ) {
        if (!document.querySelector('#sidebar-cart.active')) {
            event.preventDefault();
            open();
        }
    }
    if (!modalOpen()) return;
    if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
    }
    if (event.key === 'Tab') {
        const elements = [
            ...modal.querySelectorAll('a[href],button,input,select,[tabindex="0"]'),
        ].filter(
            (element) =>
                !element.disabled &&
                element.tabIndex >= 0 &&
                element.getClientRects().length &&
                !element.closest('[inert]'),
        );
        const first = elements[0],
            last = elements[elements.length - 1];
        if (
            event.shiftKey &&
            (document.activeElement === first || !modal.contains(document.activeElement))
        ) {
            event.preventDefault();
            last?.focus();
        } else if (
            !event.shiftKey &&
            (document.activeElement === last || !modal.contains(document.activeElement))
        ) {
            event.preventDefault();
            first?.focus();
        }
    }
});
